import { useContext, useMemo } from 'react';

import { PanoramaContext } from './PanoramaContext.js';

export function usePanoramaMethodLogger<T extends object>(target: T): T {
  const factory = useContext(PanoramaContext);
  if (factory == null) {
    throw new Error(
      'usePanoramaMethodLogger() cannot be used without a <PanoramaProvider />'
    );
  }

  const componentId = target.constructor.name;
  const clientOptions = useMemo(
    () => ({ baseParams: { componentId } }),
    [componentId]
  );

  const client = useMemo(() => {
    return factory.client(clientOptions);
  }, [factory, clientOptions]);

  return useMemo(() => {
    const cache = new Map();

    return new Proxy(target, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);

        const isAsyncMethod =
          typeof value === 'function' &&
          typeof prop === 'string' &&
          Object.hasOwn(target, prop) &&
          value.constructor.name === 'AsyncFunction';

        if (!isAsyncMethod) {
          return value;
        }

        // Cache is required, otherwise each time a method is accessed,
        // the new reference is returned, which might break the react render cycles.
        if (!cache.has(prop)) {
          const wrapped = async function (...args: unknown[]) {
            const transaction = client.transaction(prop);
            transaction.start();

            const result = await value.apply(target, args);

            transaction.finish();
            return result;
          };

          cache.set(prop, wrapped);
        }

        return cache.get(prop);
      },
    });
  }, [target, client]);
}
